import React, {useCallback, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {filterMessages} from 'components/Filters/filterMessages'
import {useFiltersForm} from 'hooks/useFiltersForm'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'
import {searchParamToFacet} from 'modules/search/constants'
import {getListOfValues} from 'modules/search/selectors'
import {isNil, takeLast} from 'ramda'
import {getFacetLabel} from 'utils/filters'

import {Pill} from '@vio/atlas/Pill'

import {PropertyTypeFilterBottomSheet} from './PropertyTypeFilterBottomSheet'

const facetLabel = 'PropertyType'

interface Props {
  isActive?: boolean
}

export const PropertyTypeFilterPill: React.FC<Props> = ({isActive = false}) => {
  const showOverlay = useShowOverlay()
  const {formatMessage} = useIntl()
  const listOfValues = useSelector(getListOfValues)

  // Store the facetKey in a ref to simulate an instance variable that won't change when the component re-renders
  // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
  const facetFilterKeys = useRef([searchParamToFacet.propertyTypes.param])
  const facetFilterKey = facetFilterKeys.current[0]
  const labelMessage = formatMessage(filterMessages.propertyType)
  const getLabel = getFacetLabel(facetLabel, listOfValues)
  const {getAppliedFilters} = useFiltersForm(facetFilterKeys.current)

  const appliedFacetFilter = getAppliedFilters()[facetFilterKey]

  const buildFacetsLabel = useCallback(() => {
    if (isNil(appliedFacetFilter) || isNil(listOfValues)) {
      return labelMessage
    }

    if (appliedFacetFilter.length === 1) {
      return getLabel(appliedFacetFilter[0]) as string
    }

    return `${getLabel(takeLast(1, appliedFacetFilter).toString())} +${
      appliedFacetFilter.length - 1
    }`
  }, [getLabel, appliedFacetFilter, listOfValues])

  const handleOnPillClick = () => {
    showOverlay({
      type: OverlayType.PropertyTypeFilter
    })
  }

  return (
    <>
      <Pill
        onClick={handleOnPillClick}
        dataId="propertyTypeFilter"
        isSelected={isActive}
        showArrow
      >
        {buildFacetsLabel()}
      </Pill>
      <PropertyTypeFilterBottomSheet />
    </>
  )
}
